import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    TextField,
    MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { axiosInstance } from '../../../api';

const CARD_SX = {
    p: 2.5,
    borderRadius: '16px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 4px 20px rgba(17,24,39,0.05)',
};

const BiometricSyncHistory = () => {
    const [subTab, setSubTab] = useState(0);
    const [jobs, setJobs] = useState([]);
    const [punches, setPunches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterMapping, setFilterMapping] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const [jobRes, punchRes] = await Promise.all([
                axiosInstance.get('/api/attendance/biometric/sync/'),
                axiosInstance.get(`/api/attendance/biometric/punches/${filterMapping ? `?mapping_status=${filterMapping}` : ''}`),
            ]);
            setJobs(jobRes.data.sync_jobs || []);
            setPunches(punchRes.data.staged_punches || []);
        } catch (err) {
            console.error('Error fetching sync history:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [filterMapping]);

    const getStatusChip = (st) => {
        if (st === 'SUCCESS') return <Chip label="Success" size="small" color="success" sx={{ fontWeight: 700 }} />;
        if (st === 'PARTIAL') return <Chip label="Partial" size="small" color="warning" sx={{ fontWeight: 700 }} />;
        if (st === 'FAILED') return <Chip label="Failed" size="small" color="error" sx={{ fontWeight: 700 }} />;
        return <Chip label={st || 'Running'} size="small" sx={{ fontWeight: 700 }} />;
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Paper elevation={0} sx={{ ...CARD_SX, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
                        Synchronization & Staged Punches
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Inspect synchronization job runs and isolated punch events received from biometric terminals.
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                    Refresh
                </Button>
            </Paper>

            <Paper elevation={0} sx={{ ...CARD_SX, p: 0, mb: 3, overflow: 'hidden' }}>
                <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ px: 2, pt: 1, borderBottom: '1px solid #f3f4f6' }}>
                    <Tab icon={<HistoryIcon />} iconPosition="start" label="Sync Jobs Audit" sx={{ fontWeight: 700, textTransform: 'none' }} />
                    <Tab icon={<FingerprintIcon />} iconPosition="start" label="Isolated Staged Punches" sx={{ fontWeight: 700, textTransform: 'none' }} />
                </Tabs>
            </Paper>

            {subTab === 0 ? (
                /* Sync Jobs Table */
                <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Job ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Device / Connector</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Trigger</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Received</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Staged</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Duplicates</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Unmapped</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Started At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}><CircularProgress size={36} /></TableCell>
                                    </TableRow>
                                ) : jobs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                            <Typography variant="body2" sx={{ color: '#6b7280' }}>No synchronization runs recorded yet.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    jobs.map((j) => (
                                        <TableRow key={j.id} hover>
                                            <TableCell sx={{ fontWeight: 700 }}>#{j.id}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{j.device_name}</Typography>
                                                {j.connector_name && <Typography variant="caption" sx={{ color: '#6b7280' }}>via {j.connector_name}</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={j.trigger_type} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                            </TableCell>
                                            <TableCell>{getStatusChip(j.status)}</TableCell>
                                            <TableCell>{j.records_received}</TableCell>
                                            <TableCell sx={{ color: '#16a34a', fontWeight: 700 }}>+{j.records_staged}</TableCell>
                                            <TableCell sx={{ color: '#6b7280' }}>{j.duplicates_skipped}</TableCell>
                                            <TableCell sx={{ color: j.unmapped_count > 0 ? '#b45309' : '#6b7280', fontWeight: j.unmapped_count > 0 ? 700 : 400 }}>
                                                {j.unmapped_count}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(j.started_at).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ) : (
                /* Staged Punches Table */
                <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
                    <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            select
                            size="small"
                            label="Filter Mapping Status"
                            value={filterMapping}
                            onChange={(e) => setFilterMapping(e.target.value)}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="">All Punches</MenuItem>
                            <MenuItem value="MAPPED">Mapped Only</MenuItem>
                            <MenuItem value="UNMAPPED">Unmapped Only</MenuItem>
                        </TextField>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Terminal User ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Mapped Employee</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Punch Timestamp</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Punch Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Mapping Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Reconciliation Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={36} /></TableCell>
                                    </TableRow>
                                ) : punches.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                            <Typography variant="body2" sx={{ color: '#6b7280' }}>No staged punches found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    punches.map((p) => (
                                        <TableRow key={p.id} hover>
                                            <TableCell>
                                                <Chip label={p.device_user_id} size="small" sx={{ fontWeight: 800, bgcolor: '#eff6ff', color: '#1d4ed8' }} />
                                            </TableCell>
                                            <TableCell>
                                                {p.employee_no ? (
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.employee_name}</Typography>
                                                        <Typography variant="caption" sx={{ color: '#6b7280' }}>ID: {p.employee_no}</Typography>
                                                    </Box>
                                                ) : (
                                                    <Chip label="Unmapped" size="small" color="warning" sx={{ fontWeight: 700 }} />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {new Date(p.event_timestamp).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={p.punch_type} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                                            </TableCell>
                                            <TableCell>{p.device_name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={p.mapping_status}
                                                    size="small"
                                                    color={p.mapping_status === 'MAPPED' ? 'success' : 'default'}
                                                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={p.reconciliation_status} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
};

export default BiometricSyncHistory;

